import './customer-overviews.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { customerOrderResponseSchema } from '@inktap/contracts/customer-orders';

import { CARD_STYLES } from '../components/card/CardTemplateLibrary';
import PhysicalCardControls from '../components/card/PhysicalCardControls';
import { request } from '../lib/api';
import { useSession } from '../session';
import { canReviewPendingOrder } from './orderEligibility';
import { formatDate, formatStatus } from './portalTypes';
import { useDashboardSummary } from './useDashboardSummary';

const formatStyle = (value: string | null | undefined) => {
  if (!value) {
    return 'Not selected';
  }
  return value
    .split(/[-_]/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

function CardDesignPortalPage() {
  const { summary, loading, error, reload } = useDashboardSummary();
  const { session } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const [previewFailures, setPreviewFailures] = useState<{ scope: string; urls: string[] }>({ scope: '', urls: [] });
  const [previewAttempt, setPreviewAttempt] = useState(0);
  const previewStage = useRef<HTMLElement>(null);
  const card = summary?.card ?? null;
  const design = card?.latestDesign ?? null;
  const order = card?.latestOrder ?? null;
  const designPreview = design?.previewImageUrl ?? null;
  const orderPreview = order?.previewImageUrl ?? null;
  const previewScope = JSON.stringify([session?.email, session?.slug, session?.expiresAt, session?.active,
    design?.id, order?.id, designPreview, orderPreview, previewAttempt]);
  const previewScopeRef = useRef(previewScope);
  previewScopeRef.current = previewScope;
  const failedUrls = previewFailures.scope === previewScope ? previewFailures.urls : [];
  const previewSource = designPreview && !failedUrls.includes(designPreview) ? 'design'
    : orderPreview && !failedUrls.includes(orderPreview) ? 'order' : null;
  const previewImage = previewSource === 'design' ? designPreview : previewSource === 'order' ? orderPreview : null;
  const styleId = previewSource === 'design' ? design?.styleId : previewSource === 'order' ? order?.styleId : null;
  const markPreviewFailed = (url: string) => {
    if (previewScopeRef.current !== previewScope) return;
    setPreviewFailures((current) => {
      const urls = current.scope === previewScope ? current.urls : [];
      return urls.includes(url) ? current : { scope: previewScope, urls: [...urls, url] };
    });
  };
  const selectedStyle = useMemo(
    () => CARD_STYLES.find((candidate) => candidate.id === styleId) ?? null,
    [styleId]
  );
  const previewCardStyle = useMemo(
    () =>
      ({
        '--card-preview-background-color': selectedStyle?.theme.backgroundColor ?? '#f8fafc',
        '--card-preview-background-image': selectedStyle?.theme.backgroundImage ?? 'none'
      }) as CSSProperties,
    [selectedStyle]
  );
  const cardStatus = card?.status ? formatStatus(card.status) : 'Not started';
  const isCheckoutPending = card?.status === 'checkout_pending' && Boolean(order);
  const editScope = JSON.stringify([location.key, session?.email, session?.slug, session?.expiresAt,
    session?.active, card?.status, order?.id]);
  const editContext = useRef({ scope: editScope, generation: 0, busy: false });
  if (editContext.current.scope !== editScope) {
    editContext.current = { scope: editScope, generation: editContext.current.generation + 1, busy: false };
  }
  const [editState, setEditState] = useState<{ scope: string; generation: number; status: 'checking' | 'unavailable' | 'failed' } | null>(null);
  const currentEditState = editState?.scope === editScope && editState.generation === editContext.current.generation ? editState.status : null;
  const editMessage = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (currentEditState === 'unavailable' || currentEditState === 'failed') editMessage.current?.focus();
  }, [currentEditState]);
  useEffect(() => () => { editContext.current.generation += 1; }, []);

  const editPendingCard = async () => {
    if (!order || !isCheckoutPending || editContext.current.busy) return;
    if (!session?.email) {
      setEditState({ scope: editScope, generation: editContext.current.generation, status: 'failed' });
      return;
    }
    const generation = editContext.current.generation;
    const valid = () => editContext.current.scope === editScope && editContext.current.generation === generation;
    editContext.current.busy = true;
    setEditState({ scope: editScope, generation, status: 'checking' });
    try {
      const payload = await request<unknown>(`/me/orders/${encodeURIComponent(order.id)}`, { cache: 'no-store' });
      if (!valid()) return;
      const parsed = customerOrderResponseSchema.safeParse(payload);
      if (!parsed.success || parsed.data.order.id !== order.id) throw new Error('Order could not be verified');
      if (!canReviewPendingOrder(parsed.data.order)) {
        setEditState({ scope: editScope, generation, status: 'unavailable' });
        return;
      }
      navigate(`/cards/design?order=${encodeURIComponent(order.id)}`);
    } catch {
      if (valid()) setEditState({ scope: editScope, generation, status: 'failed' });
    } finally {
      if (valid()) editContext.current.busy = false;
    }
  };

  return (
    <div className="dashboard-page portal-page portal-workspace card-overview">
      <header className="portal-intro">
        <h1 tabIndex={-1}>Your card design</h1>
        <p>Review your latest design, order status, and the next available action.</p>
      </header>

      {error ? <div className="overview-recovery"><div className="alert" role="alert">{error}</div><button type="button" className="button-secondary" onClick={(event) => { event.currentTarget.closest('.portal-workspace')?.querySelector('h1')?.focus(); void reload(); }}>Retry account summary</button></div> : null}

      {loading ? (
        <section className="dashboard-section" aria-live="polite">
          <p className="helper-text">Loading your card design...</p>
        </section>
      ) : summary ? (
        <>
          <div className="card-overview__workspace">
            <section ref={previewStage} tabIndex={-1} className="card-preview-stage" aria-label="Card preview">
              {previewImage ? (
                <>
                  <div className="card-preview-card" style={previewCardStyle}>
                    <img src={previewImage} alt={previewSource === 'design' ? 'Stored card image' : 'Latest order card preview'} onError={() => markPreviewFailed(previewImage)} />
                  </div>
                  <p className="card-preview-stage__caption">{previewSource === 'design' ? 'Stored card image. It may not reflect your current edits.' : 'Latest order preview'}</p>
                </>
              ) : (
                <div className="card-preview-fallback">
                  <span className="brand-wordmark"><span className="brand-wordmark__ink">ink</span><span className="brand-wordmark__tap">tap</span></span>
                  <p>{designPreview || orderPreview ? 'Your card preview is unavailable. You can open the card designer.' : 'No card preview is available yet. Open the card designer to view your design.'}</p>
                </div>
              )}
              {failedUrls.length > 0 ? <button type="button" className="button-secondary card-preview-stage__retry"
                onClick={() => { previewStage.current?.focus({ preventScroll: true }); setPreviewAttempt((attempt) => attempt + 1); }}>Retry card previews</button> : null}
            </section>

            <section className="card-detail-rail">
              <div className="card-detail-rail__status-summary"><span>Card progress</span><strong>{cardStatus}</strong></div>
              <section aria-labelledby="card-design-details">
                <h2 id="card-design-details" tabIndex={-1}>Design record</h2>
                {design ? <dl>
                  <div><dt>Style</dt><dd>{formatStyle(design.styleId)}</dd></div>
                  <div><dt>Quantity</dt><dd>{design.quantity ?? 'Not set'}</dd></div>
                  <div><dt>Last edited</dt><dd>{formatDate(design.updatedAt)}</dd></div>
                </dl> : <p>No card design record yet.</p>}
              </section>
              <section aria-labelledby="card-order-details">
                <h2 id="card-order-details">Latest order</h2>
                {order ? <dl>
                  <div><dt>Status</dt><dd>{formatStatus(order.status)}</dd></div>
                  <div><dt>Material</dt><dd>{formatStyle(order.material)}</dd></div>
                  <div><dt>Style</dt><dd>{formatStyle(order.styleId)}</dd></div>
                  <div><dt>Quantity</dt><dd>{order.quantity}</dd></div>
                </dl> : <p>No card order yet.</p>}
              </section>
              <div className="card-detail-rail__actions">
                {isCheckoutPending ? (
                  <>
                    <p>Review your stored card amount in Orders.</p>
                    <Link className="btn-primary" to="/orders">Review checkout</Link>
                    <button type="button" className="button-secondary" disabled={currentEditState === 'checking'}
                      onClick={(event) => { event.currentTarget.closest('.card-detail-rail')?.querySelector('h2')?.focus(); void editPendingCard(); }}>
                      {currentEditState === 'checking' ? 'Checking order...' : 'Edit card'}
                    </button>
                    {currentEditState === 'unavailable' ? <p ref={editMessage} tabIndex={-1} role="alert">This order cannot be edited right now. Review its current status in Orders.</p> : null}
                    {currentEditState === 'failed' ? <p ref={editMessage} tabIndex={-1} role="alert">Unable to confirm this order. Review its current status in Orders before editing.</p> : null}
                  </>
                ) : (
                  <Link className="btn-primary" to="/cards/design">
                    {order ? 'Design another card' : design ? 'Edit design' : 'Design your first card'}
                  </Link>
                )}
                {order ? <Link className="button-secondary" to="/orders">View order</Link> : null}
                <a className="button-secondary" href="#physical-card-controls-title">Manage physical cards</a>
              </div>
            </section>
          </div>

          <section className="card-production-strip">
            <div>
              <span>Order</span>
              <strong>{order ? `Your latest card is ${formatStatus(order.status).toLowerCase()}.` : 'No card order yet.'}</strong>
              <p>{order ? 'You can follow production and shipping from Orders.' : 'Save a design, then review it before checkout.'}</p>
            </div>
            <div>
              <span>Digital proof</span>
              <strong>{summary.plan.entitlements.canDownloadProof ? 'Proof downloads are available.' : 'Proof downloads are a paid-plan benefit.'}</strong>
              <p>{summary.plan.entitlements.canDownloadProof ? 'Create and download a proof in the card designer.' : 'Boost, Secure, and Premium include downloadable proofs.'}</p>
            </div>
            {summary.plan.entitlements.canDownloadProof ? <Link to="/cards/design">Open card designer</Link> : <Link to="/plan-billing">Compare plans</Link>}
          </section>
          <PhysicalCardControls />
        </>
      ) : null}
    </div>
  );
}

export default CardDesignPortalPage;
